<template>
	<UiModalMobile
		v-model="modelValue"
		class="modal__mobile"
		:titleBtnSave="$t('')"
		:isDontNeedCloseWhenSave="true"
		@save="saveHandler"
		:is-loading-btn-save="props.isLoading"
		:z-index="zIndex"
		:is-need-btn-save="false"
	>
		<TypographyKTypographyBody2
			color="var(--color_font_black_wite)"
			font-type="bold"
		>
			{{ $t('Список СНТ') }}
		</TypographyKTypographyBody2>
		<div class="wrapper">
			<div
				v-if="!loading.getTalonPhoto"
				class="talon-wrapper"
			>
				<div class="button-wrapper">
					<UiButton
						label="СНТ (краткий)"
						:color="activeType === 'Simple' ? ButtonType.primary : ButtonType.outline"
						:size="ButtonSizeType.small"
						@click="setCurrentType('Simple')"
					>
					</UiButton>
					<UiButton
						label="СНТ (детальный)"
						:color="activeType === 'Detailed' ? ButtonType.primary : ButtonType.outline"
						:size="ButtonSizeType.small"
						@click="setCurrentType('Detailed')"
					>
					</UiButton>
				</div>
				<div class="snt-wrapper">
					<div class="header">
						<TypographyKTypographyBody4 font-type="medium">Список СНТ</TypographyKTypographyBody4>
						<UiButtonIcon
							background="transparent"
							padding="2px"
							@click="copySnt"
						>
							<Icon
								name="tabler:copy-filled"
								color="#2E4A85"
							/>
						</UiButtonIcon>
					</div>
					<div
						class="body"
						v-html="sntDetail"
					></div>
				</div>
			</div>
			<div
				v-else-if="false"
				class="notification-empty"
			>
				Список СНТ пуст
			</div>
			<div
				v-else
				class="loader"
			>
				<UiLoader
					:circle-width="2"
					:radius="20"
					:color="'var(--primary)'"
				/>
			</div>
		</div>
		<!-- <template #custom-actions> </template> -->
	</UiModalMobile>
</template>

<script setup lang="ts">
	import { TYPE_COMMENT } from '~/const/ui';
	import { getDateInLocaleString } from '~/helpers/date';
	import { VALIDATE_RULES, validator } from '~/helpers/validate';
	import { ButtonSizeType, ButtonType } from '~~/shared/interfaces/ui';
	import type { ITrip } from '~~/shared/interfaces/truck';
	import { useLongHaulTripStore } from '~/store/longHaulTrip.store';
	import { useNotificationStore } from '~/store/notification.store';
	import { AlertsTypes } from '~/const/notification';

	const emits = defineEmits(['save']);
	const modelValue = defineModel('modelValue', { required: true, type: Boolean });
	const props = withDefaults(
		defineProps<{
			isLoading?: boolean;
			zIndex?: number;
			currentTrip: ITrip;
		}>(),
		{}
	);
	const notification = useNotificationStore();
	const loading = reactive({
		uploadFile: false,
		getTalonPhoto: false
	});

	const activeType = ref<'Simple' | 'Detailed'>('Simple');
	const sntDetail = ref<string>('');

	const longHaulTrip = useLongHaulTripStore();

	const typeCommentComputed = computed(() => {
		return Object.values(TYPE_COMMENT);
	});
	const importance = ref(typeCommentComputed.value[0] || null);

	const errorField = ref({
		text: { status: false, msg: '' }
	});

	const setCurrentType = (type: 'Simple' | 'Detailed') => {
		activeType.value = type;

		setTimeout(() => {
			getSntList();
		});
	};

	const clearError = (type: string) => {
		errorField.value = { ...errorField.value, [type]: { status: false } };
	};

	const validateHandler = () => {
		const arrayRules: Array<{
			field: string;
			value: string | null;
			rules: VALIDATE_RULES[];
		}> = [{ field: 'text', value: text.value, rules: [VALIDATE_RULES.required] }];
		arrayRules.forEach((item) => {
			errorField.value = { ...errorField.value, [item.field]: validator(item.value, item.rules)[0] };
		});
	};

	const saveHandler = () => {
		validateHandler();
		if (!Object.values(errorField.value).find((item) => item.status)) {
			emits('save', {
				cargoId: props.currentCargo.id,
				text: text.value,
				importance: importance.value?.id
			});
		}
	};

	const copySnt = () => {
		navigator.clipboard.writeText(`${sntDetail.value}`);
		notification.addNewAlert('ID скопирован в буфер обмена', 2000, AlertsTypes.SUCCESS);
	};

	const getSntList = async () => {
		loading.getTalonPhoto = true;
		const response = await longHaulTrip.getSntReportByTrip({
			tripId: props.currentTrip?.id,
			sntReport: activeType.value
		});

		if (response.status === 'success') {
			sntDetail.value = response.data?.data || '';
		}

		loading.getTalonPhoto = false;
	};

	getSntList();
</script>

<style scoped lang="scss">
	.snt-wrapper {
		display: flex;
		flex-direction: column;
		background-color: #eaedf5;
		margin-top: 14px;
		border-radius: 12px;
		padding: 12px;
		height: 100%;
		min-height: fit-content;

		.body {
			background-color: white;
			width: 100%;
			height: 100%;
			border-radius: 12px;
			margin-top: 8px;
			padding: 8px 12px;
			white-space: pre-line;
			overflow-wrap: break-word;
			word-break: break-word;
		}

		.header {
			display: flex;
			justify-content: space-between;
			align-items: center;
		}
	}
	.talon-wrapper {
		display: flex;
		flex-direction: column;
		height: 100%;
	}
	.button-wrapper {
		display: flex;
		gap: 8px;
		justify-content: flex-end;
		width: 100%;
	}

	.loader {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
	}
	.notification-empty {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		font-size: 14px;
		font-weight: 700;
		color: var(--color_font_black_wite);
	}
	.btn-read-all {
		position: relative;
		margin-left: auto;
		color: #2e4a85;
		font-size: 10px;
		font-weight: 500;
		text-decoration: dotted;
		cursor: pointer;

		&:hover {
			&::after {
				content: '';
				position: absolute;
				left: 0;
				bottom: 0;
				width: 100%;
				height: 1px;
				border-bottom: 1px solid #2e4a85;
			}
		}
	}
	.notification__body {
		.message {
			background-color: #eaedf5;
			word-break: break-word;
			padding: 8px 14px;
			border-radius: 12px;
			margin-top: 8px;
		}
	}
	.status {
		width: 9px;
		height: 9px;
		border-radius: 50%;
		background-blend-mode: overlay;
		background-color: #14ab87;

		&.--is-read {
			background: linear-gradient(260.49deg, rgba(255, 255, 255, 0) -0.38%, rgba(255, 255, 255, 0.4) 153.72%);
			background: #37547b4d;
		}
	}
	.modal__mobile {
		display: flex;
		flex-direction: column;

		.ui-select__item {
			display: flex;
			align-items: center;

			.icon {
				margin-right: 5px;
			}
		}

		.label-select {
			margin-left: auto;
		}

		.upper-limit-clue {
			max-width: 300px;
			@media (max-width: $sm) {
				max-width: 200px;
			}
		}

		.input__secret {
			display: flex;
			flex-direction: column;
			gap: 8px;
			margin-bottom: -10px;
		}

		.wrapper {
			display: flex;
			flex-direction: column;
			overflow: auto;
			gap: 17px;
			margin-top: 14px;
			padding-right: 10px;
			margin-right: -10px;
			padding-bottom: 5px;
			padding-top: 5px;
			height: 100%;
		}
	}
</style>
